import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import { Base_Url } from '../config';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

const UploadModal = ({ modal, closeModal, fetchFaculty, editObj }) => {
  const [formData, setFormData] = useState({
    file: null,
    name: '',
    sectionTitle: '',
    researchInterest: '',
    email: '',
    office: '',
    phone: '',
    webpageurl: ''
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const userfromwebmaster = useSelector((state) => state.user);
  const facultstaff = useSelector((state) => state.facultyuser);

  useEffect(() => {
    if (editObj) {
      setFormData({
        file: null,
        name: editObj.name || '',
        sectionTitle: editObj.sectionTitle || '',
        researchInterest: editObj.researchInterest || '',
        email: editObj.email || '',
        office: editObj.office || '',
        phone: editObj.phone || '',
        webpageurl: editObj.webpage_url || ''
      });
    }
  }, [editObj]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value
    });
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    // Only require file for new entries, not edits
    if (!editObj && !formData.file) newErrors.file = 'This field is required';
    if (!formData.name) newErrors.name = 'This field is required';
    if (!formData.sectionTitle) newErrors.sectionTitle = 'This field is required';
    if (!formData.researchInterest) newErrors.researchInterest = 'This field is required';
    if (!formData.email) {
      newErrors.email = 'This field is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone) newErrors.phone = 'This field is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const validationErrors = validate();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const data = new FormData();
    if (formData.file) {
      data.append('image', formData.file);
    }
    data.append('name', formData.name);
    data.append('designation', formData.sectionTitle);
    data.append('summary', formData.researchInterest);
    data.append('email', formData.email);
    data.append('office', formData.office);
    data.append('phone', formData.phone);
    data.append('weblink', formData.webpageurl);

    // Safer created_by handling
    const created_by = userfromwebmaster?.userExists && userfromwebmaster?.userData?.userid 
      ? userfromwebmaster.userData.userid
      : facultstaff?.facultyprofileData?.user_id;

    if (!created_by) {
      setServerError('User ID not found. Please try logging in again.');
      return;
    }
    
    data.append('created_by', created_by);

    try {
      const response = await axios.post(`${Base_Url}/profile`, data);
      if (response.data && response.data.message) {
        closeModal();
        fetchFaculty();
        setFormData({
          file: null,
          name: '',
          sectionTitle: '',
          researchInterest: '',
          email: '',
          office: '',
          phone: '',
          webpageurl: ''
        });
      }
    } catch (error) {
      console.error('Error uploading file and inserting data:', error);
      setServerError(
        error.response?.data?.error || 
        'An error occurred while submitting the form. Please try again.'
      );
    }
  };

  return (
    <Modal show={modal} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editObj ? 'Edit' : 'Add'} Faculty Member</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {serverError && (
          <Alert variant="danger" className="mb-3">
            {serverError}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="formFile">
            <Form.Label>Upload Photo {!editObj && <span className="text-danger">*</span>}</Form.Label>
            <Form.Control
              type="file"
              name="file"
              onChange={handleChange}
              accept="image/png, image/jpeg, image/jpg, image/gif"
            />
            {errors.file && <span className="text-danger">{errors.file}</span>}
          </Form.Group>
          
          <Form.Group controlId="formName" className="mb-1">
            <Form.Label className="mt-2">Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && <span className="text-danger">{errors.name}</span>}
          </Form.Group>

          <Form.Group controlId="formSectionTitle" className="mb-1">
            <Form.Label className="mt-2">
              Designation <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="sectionTitle"
              placeholder="Designation"
              value={formData.sectionTitle}
              onChange={handleChange}
            />
            {errors.sectionTitle && <span className="text-danger">{errors.sectionTitle}</span>}
          </Form.Group>

          <Form.Group controlId="formResearchInterest" className="mb-1">
            <Form.Label className="mt-2">
              Summary <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              name="researchInterest"
              placeholder="Summary"
              value={formData.researchInterest}
              onChange={handleChange}
              rows={3}
            />
            {errors.researchInterest && <span className="text-danger">{errors.researchInterest}</span>}
          </Form.Group>

          <Form.Group controlId="formEmail" className="mb-1">
            <Form.Label className="mt-2">Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <span className="text-danger">{errors.email}</span>}
          </Form.Group>

          <Form.Group controlId="formPhone" className="mb-1">
            <Form.Label className="mt-2">Phone <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="phone"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleChange}
            />
            {errors.phone && <span className="text-danger">{errors.phone}</span>}
          </Form.Group>

          <Form.Group controlId="formWebpageUrl" className="mb-1">
            <Form.Label className="mt-2">Webpage URL</Form.Label>
            <Form.Control
              type="text"
              name="webpageurl"
              placeholder="Webpage URL"
              value={formData.webpageurl}
              onChange={handleChange}
            />
          </Form.Group>

          <Button variant="primary" type="submit" className="mt-3">
            {editObj ? 'Update' : 'Submit'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default UploadModal;